import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from './firebase';
import type { DrugInfo, CEEntry, ChatSession, Activity } from '../types';

export interface ClinicianSyncData {
  favorites: string[];
  recentlyViewed: string[];
  customDrugs: DrugInfo[];
  ceEntries: CEEntry[];
  recentActivities: Activity[];
  quickAccessTools: string[];
  lastSyncedAt?: string;
}

// In-memory debounce timers per UID
const debounceTimers: Record<string, any> = {};

/**
 * Helper to get local clinician sync data
 */
export const getLocalClinicianData = (uid: string): ClinicianSyncData => {
  try {
    const favorites = JSON.parse(localStorage.getItem(`drugFavorites_${uid}`) || '[]');
    const recentlyViewed = JSON.parse(localStorage.getItem(`drugRecents_${uid}`) || '[]');
    const customDrugs = JSON.parse(localStorage.getItem(`userDrugIndex_${uid}`) || '[]');
    const ceEntries = JSON.parse(localStorage.getItem(`drrx_ce_credits_${uid}`) || localStorage.getItem('drrx_ce_credits') || '[]');
    const recentActivities = JSON.parse(localStorage.getItem(`drrx_recentActivities_${uid}`) || '[]');
    const quickAccessTools = JSON.parse(localStorage.getItem(`drrx_quickAccessTools_v2_${uid}`) || '[]');

    return {
      favorites: Array.isArray(favorites) ? favorites : [],
      recentlyViewed: Array.isArray(recentlyViewed) ? recentlyViewed : [],
      customDrugs: Array.isArray(customDrugs) ? customDrugs : [],
      ceEntries: Array.isArray(ceEntries) ? ceEntries : [],
      recentActivities: Array.isArray(recentActivities) ? recentActivities : [],
      quickAccessTools: Array.isArray(quickAccessTools) ? quickAccessTools : [],
    };
  } catch (err) {
    console.error('[UserSync] Error reading local data:', err);
    return {
      favorites: [],
      recentlyViewed: [],
      customDrugs: [],
      ceEntries: [],
      recentActivities: [],
      quickAccessTools: [],
    };
  }
};

/**
 * Persist merged data to localStorage
 */
export const saveLocalClinicianData = (uid: string, data: Partial<ClinicianSyncData>): void => {
  try {
    if (data.favorites !== undefined) {
      localStorage.setItem(`drugFavorites_${uid}`, JSON.stringify(data.favorites));
    }
    if (data.recentlyViewed !== undefined) {
      localStorage.setItem(`drugRecents_${uid}`, JSON.stringify(data.recentlyViewed));
    }
    if (data.customDrugs !== undefined) {
      localStorage.setItem(`userDrugIndex_${uid}`, JSON.stringify(data.customDrugs));
    }
    if (data.ceEntries !== undefined) {
      localStorage.setItem(`drrx_ce_credits_${uid}`, JSON.stringify(data.ceEntries));
      localStorage.setItem('drrx_ce_credits', JSON.stringify(data.ceEntries));
    }
    if (data.recentActivities !== undefined) {
      localStorage.setItem(`drrx_recentActivities_${uid}`, JSON.stringify(data.recentActivities));
    }
    if (data.quickAccessTools !== undefined) {
      localStorage.setItem(`drrx_quickAccessTools_v2_${uid}`, JSON.stringify(data.quickAccessTools));
    }
  } catch (err) {
    console.error('[UserSync] Error writing to localStorage:', err);
  }
};

/**
 * Pull cloud data from Firestore and merge with local data.
 * Returns the resolved merged ClinicianSyncData.
 */
export const syncClinicianDataFromCloud = async (uid: string): Promise<ClinicianSyncData> => {
  const localData = getLocalClinicianData(uid);
  if (!uid) return localData;

  try {
    const syncDocRef = doc(db, 'customers', uid, 'user_sync', 'clinical_data');
    const docSnap = await getDoc(syncDocRef);

    if (!docSnap.exists()) {
      // First time sync on cloud: push local data up to Firestore
      await setDoc(syncDocRef, {
        ...localData,
        lastSyncedAt: new Date().toISOString()
      });
      return localData;
    }

    const cloudData = docSnap.data() as Partial<ClinicianSyncData>;

    // Merge strategy: Union arrays with unique IDs or strings
    const mergedFavorites = Array.from(new Set([
      ...(localData.favorites || []),
      ...(cloudData.favorites || [])
    ]));

    const mergedRecents = Array.from(new Set([
      ...(localData.recentlyViewed || []),
      ...(cloudData.recentlyViewed || [])
    ])).slice(0, 10);

    // Merge custom drugs by ID
    const drugMap = new Map<string, DrugInfo>();
    (cloudData.customDrugs || []).forEach(d => { if (d && d.id) drugMap.set(d.id, d); });
    (localData.customDrugs || []).forEach(d => { if (d && d.id) drugMap.set(d.id, d); });
    const mergedCustomDrugs = Array.from(drugMap.values());

    // Merge CE entries by ID
    const ceMap = new Map<string, CEEntry>();
    (cloudData.ceEntries || []).forEach(e => { if (e && e.id) ceMap.set(e.id, e); });
    (localData.ceEntries || []).forEach(e => { if (e && e.id) ceMap.set(e.id, e); });
    const mergedCE = Array.from(ceMap.values()).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    // Merge Quick Access Tools (prefer cloud if exists, else local)
    const mergedQuickAccess = cloudData.quickAccessTools && cloudData.quickAccessTools.length > 0
      ? cloudData.quickAccessTools
      : localData.quickAccessTools;

    const mergedData: ClinicianSyncData = {
      favorites: mergedFavorites,
      recentlyViewed: mergedRecents,
      customDrugs: mergedCustomDrugs,
      ceEntries: mergedCE,
      recentActivities: localData.recentActivities.length > 0 ? localData.recentActivities : (cloudData.recentActivities || []),
      quickAccessTools: mergedQuickAccess,
      lastSyncedAt: new Date().toISOString()
    };

    // Save back to local storage
    saveLocalClinicianData(uid, mergedData);

    // Update cloud with any merged local additions
    await setDoc(syncDocRef, mergedData, { merge: true });

    // Notify components
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('drrx_cloud_sync_completed', { detail: mergedData }));
    }

    return mergedData;
  } catch (error) {
    console.warn('[UserSync] Could not sync with Firestore (running in offline mode):', error);
    return localData;
  }
};

/**
 * Debounced push to Firestore cloud sync
 */
export const pushClinicianDataToCloud = (uid: string, partialData: Partial<ClinicianSyncData>): void => {
  if (!uid) return;

  // Immediately save locally
  saveLocalClinicianData(uid, partialData);

  // Debounce cloud write by 1200ms
  if (debounceTimers[uid]) {
    clearTimeout(debounceTimers[uid]);
  }

  debounceTimers[uid] = setTimeout(async () => {
    try {
      const syncDocRef = doc(db, 'customers', uid, 'user_sync', 'clinical_data');
      await setDoc(syncDocRef, {
        ...partialData,
        lastSyncedAt: new Date().toISOString()
      }, { merge: true });
      console.log('[UserSync] Successfully synced clinical data to cloud.');
    } catch (err) {
      console.warn('[UserSync] Failed to persist data to cloud (will retry next session):', err);
    }
  }, 1200);
};
